import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { day, et, toneOf, type KN, type Latest } from "@/platform/data";

// The platform's visual system (24 Oct 2026): a research terminal more than a
// brochure. A wide grid, tables as the main element, every number in mono with
// tabular figures, mono small-caps labels, one accent, hairlines over shadows.

export const W = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cn("mx-auto w-full max-w-[1320px] px-5 sm:px-8", className)}>{children}</div>
);

export const Mono = ({ children, className }: { children: ReactNode; className?: string }) => (
  <span className={cn("font-mono [font-variant-numeric:tabular-nums]", className)}>{children}</span>
);

/** Mono small caps: "FILINGS · UNITED STATES". */
export const Label = ({ children, className }: { children: ReactNode; className?: string }) => (
  <p className={cn("text-[12.5px] font-medium text-muted-foreground", className)}>{children}</p>
);

const NAV: [string, string][] = [
  ["/data", "US market"],
  ["/data/datasets", "Datasets"],
  ["/data/companies", "Companies"],
  ["/data/events", "Events"],
  ["/data/developers", "Developers"],
  ["/data/licence", "Licence"],
];

/** The platform's own bar under the site header: where you are in the data, and the one action. */
export const PlatformBar = () => {
  const { pathname } = useLocation();
  const on = (to: string) => (to === "/data" ? pathname === "/data" : pathname === to || pathname.startsWith(to + "/"));
  return (
    <div className="sticky top-[56px] z-40 border-b border-[var(--sn-border)] bg-[hsl(var(--card))]/95 backdrop-blur-xl lg:top-[60px]">
      <W className="flex h-[46px] items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-5">
          <Link to="/data" className="hidden shrink-0 items-center gap-2 font-mono text-[11.5px] font-semibold uppercase tracking-[0.14em] text-foreground sm:flex">
            <span className="inline-block h-2 w-2 rounded-full bg-[var(--sn-up)] shadow-[0_0_0_3px_color-mix(in_srgb,var(--sn-up)_20%,transparent)]" />
            SN Data
          </Link>
          <nav aria-label="Data" className="flex min-w-0 gap-0.5 overflow-x-auto [scrollbar-width:none]">
            {NAV.map(([to, label]) => (
              <Link key={to} to={to} className={cn("whitespace-nowrap rounded-md px-2.5 py-1.5 text-[13px]", on(to) ? "bg-[color-mix(in_srgb,var(--sn-fg)_7%,transparent)] font-semibold text-foreground" : "text-muted-foreground hover:text-foreground")}>
                {label}
              </Link>
            ))}
          </nav>
        </div>
        <Link to="/data#enquire" className="hidden shrink-0 rounded-md bg-primary px-3 py-1.5 text-[13px] font-semibold text-primary-foreground hover:brightness-110 md:inline-block">
          Tell us what you need
        </Link>
      </W>
    </div>
  );
};

export const Crumbs = ({ items }: { items: [to: string | null, label: string][] }) => (
  <nav aria-label="Breadcrumb" className="font-mono text-[11.5px] text-muted-foreground">
    {items.map(([to, label], i) => (
      <span key={i}>
        {i > 0 && <span className="px-1.5 opacity-60">/</span>}
        {to ? <Link to={to} className="hover:text-foreground">{label}</Link> : <span className="text-foreground">{label}</span>}
      </span>
    ))}
  </nav>
);

/** The figures row under a page's heading: label, big mono number, one line of context. */
export const Kpis = ({ items, className }: { items: { label: string; value: ReactNode; foot?: ReactNode; to?: string }[]; className?: string }) => (
  <div className={cn("snd-card grid grid-cols-2 gap-px overflow-hidden rounded-[10px] border border-[var(--sn-border)] bg-[var(--sn-border)] md:grid-cols-3 xl:grid-cols-6", className)}>
    {items.map((k, i) => {
      const body = (
        <>
          <p className="text-[12.5px] text-muted-foreground">{k.label}</p>
          <p className="mt-1.5 font-mono text-[21px] font-medium leading-none tracking-[-0.02em] text-foreground [font-variant-numeric:tabular-nums]">{k.value}</p>
          {k.foot && <p className="mt-2 text-[12px] leading-[1.4] text-muted-foreground">{k.foot}</p>}
        </>
      );
      const cls = cn("block bg-[hsl(var(--card))] px-4 py-3.5");
      return k.to ? <Link key={k.label} to={k.to} className={cn(cls, "hover:bg-[color-mix(in_srgb,var(--sn-accent)_4%,hsl(var(--card)))]")}>{body}</Link> : <div key={k.label} className={cls}>{body}</div>;
    })}
  </div>
);

/** A numbered section heading: "02  Newest records". */
// answer: the section's one-sentence fact from the page's own file (companyAnswers), above the lead
export const Sec = ({ n, title, lead, answer, right, id, children, className }: { n: number; title: ReactNode; lead?: ReactNode; answer?: ReactNode; right?: ReactNode; id?: string; children: ReactNode; className?: string }) => (
  <section id={id} className={cn("scroll-mt-[160px] pt-8 md:pt-10", className)}>
    <div className="mb-4 flex flex-wrap items-end justify-between gap-x-8 gap-y-2 border-t border-[var(--sn-border-strong)] pt-4">
      <div className="min-w-0">
        <h2 className="text-[19px] font-semibold leading-[1.2] tracking-[-0.01em] text-foreground">{title}</h2>
        {answer && <p className="mt-1.5 max-w-[86ch] text-[14.5px] leading-[1.55] text-foreground">{answer}</p>}
        {lead && <p className="mt-1.5 max-w-[86ch] text-[13.5px] leading-[1.55] text-muted-foreground">{lead}</p>}
      </div>
      {right}
    </div>
    {children}
  </section>
);

export const Panel = ({ title, right, children, className, foot }: { title?: ReactNode; right?: ReactNode; children: ReactNode; className?: string; foot?: ReactNode }) => (
  <div className={cn("snd-card min-w-0 overflow-hidden rounded-[10px] border border-[var(--sn-border)] bg-[hsl(var(--card))]", className)}>
    {(title || right) && (
      <div className="flex items-center justify-between gap-3 rounded-t-[10px] border-b border-[var(--sn-border)] bg-[var(--snd-band)] px-4 py-2">
        {title && <div className="text-[13px] font-semibold text-foreground">{title}</div>}
        {right}
      </div>
    )}
    {children}
    {foot && <div className="border-t border-[var(--sn-border)] px-4 py-2.5 text-[13px]">{foot}</div>}
  </div>
);

/** A company's mark (24 Oct 2026): the full ticker when it is four characters or fewer, so it can never
 * read as another company's ticker (Apple is AAPL, never AAP); a longer ticker shows the name's initials.
 * The tint is picked from the ticker, so a company looks the same everywhere. */
const SKIP = new Set(["the", "of", "and", "inc", "corp", "co", "ltd", "plc", "group", "holdings", "company", "corporation", "limited", "sa", "nv", "ag", "se"]);
export const markText = (symbol: string, name?: string) => {
  if (symbol.length <= 4) return symbol;
  const words = (name || "").replace(/[^A-Za-z0-9 ]/g, " ").split(/\s+/).filter((w) => w && !SKIP.has(w.toLowerCase()));
  return (words.slice(0, 2).map((w) => w[0].toUpperCase()).join("") || symbol.slice(0, 2));
};
export const Monogram = ({ symbol, name, size = 28 }: { symbol: string; name?: string; size?: number }) => {
  const txt = markText(symbol, name);
  return (
    <span aria-hidden className="inline-flex shrink-0 items-center justify-center rounded-[3px] border border-[var(--sn-border-strong)] bg-[hsl(var(--card))] font-mono font-semibold tracking-[-0.02em] text-foreground" style={{ width: size, height: size, fontSize: size * (txt.length >= 4 ? 0.28 : txt.length === 3 ? 0.33 : 0.38) }}>
      {txt}
    </span>
  );
};

export const Chip = ({ kind }: { kind: string }) => {
  // a 13F move reads the same way: bought more is up, sold is down
  const dir = /purchase|^new position$|^added$/i.test(kind) ? "var(--sn-up)" : /^sale|^sold out$|^trimmed$/i.test(kind) ? "var(--sn-down)" : null;
  return <span className="whitespace-nowrap text-[13px]" style={{ color: dir || "var(--sn-fg2)" }}>{kind}</span>;
};

export const companyPath = (symbol: string) => `/data/companies/${symbol.toLowerCase().replace(/\./g, "-")}`;

/** The newest records, one line each: company, what came in, when. */
export const FeedTable = ({ rows, showCompany = true, dense = false }: { rows: Latest[]; showCompany?: boolean; dense?: boolean }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[720px] border-collapse text-[13.5px]">
      <thead>
        <tr className="border-b border-[var(--sn-border-strong)] bg-[var(--snd-band)] text-[12px] font-semibold text-[var(--sn-fg2)]">
          {showCompany && <th className="px-4 py-2.5 text-left font-medium">Company</th>}
          <th className="px-3 py-2.5 text-left font-medium">Kind</th>
          <th className="px-3 py-2.5 text-left font-medium">What it says</th>
          <th className="px-4 py-2.5 text-right font-medium">Filed</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-[var(--sn-border)] last:border-b-0 hover:bg-[color-mix(in_srgb,var(--sn-accent)_4%,transparent)]">
            {showCompany && (
              <td className={cn("px-4", dense ? "py-2" : "py-2.5")}>
                <Link to={companyPath(r.symbol)} className="flex min-w-0 items-center gap-2.5 hover:text-primary">
                  <Monogram symbol={r.symbol} name={r.name} size={dense ? 24 : 28} />
                  <span className="min-w-0">
                    <span className="block max-w-[230px] truncate font-medium text-foreground">{r.name}</span>
                    <span className="block font-mono text-[11px] text-muted-foreground">{r.symbol}</span>
                  </span>
                </Link>
              </td>
            )}
            <td className="px-3 py-2.5 align-middle"><Chip kind={r.kind} /></td>
            <td className="max-w-[520px] px-3 py-2.5 align-middle text-[13px] text-[var(--sn-fg2)] [&>*]:line-clamp-2">
              {r.url ? <a href={r.url} target="_blank" rel="noopener noreferrer" className="hover:text-primary">{r.what}</a> : r.what}
            </td>
            <td className="whitespace-nowrap px-4 py-2.5 text-right align-middle font-mono text-[12px] text-muted-foreground [font-variant-numeric:tabular-nums]">
              {(r.accepted && et(r.accepted)) || day(r.at)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** Counts by kind: a name, a hairline bar to scale, the exact count. */
export const Breakdown = ({ items, total, to }: { items: KN[]; total?: number; to?: (k: string) => string | undefined }) => {
  const max = Math.max(1, ...items.map((x) => x.n));
  return (
    <ul className="divide-y divide-[var(--sn-border)]">
      {items.map((x) => {
        const href = to?.(x.k);
        const name = href ? <Link to={href} className="text-primary hover:underline">{x.k}</Link> : <span className="text-foreground">{x.k}</span>;
        return (
          <li key={x.k} className="px-3 py-[5px]">
            <div className="flex items-baseline justify-between gap-3 text-[12.5px]">
              {name}
              <Mono className="text-[12.5px] text-[var(--sn-fg2)]">{x.n.toLocaleString("en-US")}{total ? <span className="ml-2 text-muted-foreground">{((x.n / total) * 100).toFixed(1)}%</span> : null}</Mono>
            </div>
            <div className="mt-1 h-[2px] bg-[var(--sn-grid)]"><div className="h-full rounded-full" style={{ width: `${(x.n / max) * 100}%`, background: "#1d4ed8", opacity: 0.75 }} /></div>
          </li>
        );
      })}
    </ul>
  );
};

/** Columns by year (or day), always from zero: the exact value on hover, a label every few columns.
 * fmt writes the axis and the hover in the chart's units ($416.2B, 1,204 filings); noun says what a column holds. */
export const ColumnChart = ({ data, height = 180, color = "var(--sn-c1)", label = (k: string) => k, every = 1, fmt = (v: number) => v.toLocaleString("en-US"), axis, noun = "count" }: { data: [string, number][]; height?: number; color?: string; label?: (k: string) => string; every?: number; fmt?: (v: number) => string; axis?: (v: number) => string; noun?: "count" | "figure" }) => {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...data.map((d) => d[1]));
  const step = useMemo(() => { const p = Math.pow(10, Math.floor(Math.log10(max))); const m = max / p; return (m <= 2 ? 0.5 : m <= 5 ? 1 : 2) * p; }, [max]);
  const top = Math.ceil(max / step) * step;
  const grid = Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);
  const ax = axis || ((g: number) => (g >= 1000 ? `${+(g / 1000).toFixed(1)}K` : String(g)));
  const h = hover == null ? null : data[hover];
  return (
    <div className="relative px-4 pb-3 pt-4">
      <div className="mb-2 h-[18px] font-mono text-[11.5px] text-muted-foreground">
        {h ? <><span className="text-foreground">{label(h[0])}</span> · {fmt(h[1])}</> : <span className="sr-only">Hover a column for its exact {noun}</span>}
      </div>
      <div className="relative flex" style={{ height }}>
        <div className="relative w-[52px] shrink-0">
          {grid.map((g) => (
            <span key={g} className="absolute right-2 -translate-y-1/2 font-mono text-[10px] text-muted-foreground" style={{ top: `${100 - (g / top) * 100}%` }}>{ax(g)}</span>
          ))}
        </div>
        <div className="relative flex flex-1 items-end gap-[2px] border-b border-[var(--sn-border-strong)]">
          {grid.map((g) => <div key={g} className="pointer-events-none absolute inset-x-0 border-t border-[var(--sn-grid)]" style={{ bottom: `${(g / top) * 100}%` }} />)}
          {data.map(([k, v], i) => (
            <div key={k} className="relative flex h-full flex-1 items-end" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              <div className="w-full transition-opacity" style={{ height: `${(Math.max(0, v) / top) * 100}%`, background: color, opacity: hover == null || hover === i ? 1 : 0.45 }} />
            </div>
          ))}
        </div>
      </div>
      <div className="ml-[52px] mt-1.5 flex gap-[2px]">
        {data.map(([k], i) => <span key={k} className="flex-1 text-center font-mono text-[10px] text-muted-foreground">{i % every === 0 || i === data.length - 1 ? label(k) : ""}</span>)}
      </div>
    </div>
  );
};

/** A request and its answer, copyable. */
export const Code = ({ title, children }: { title: string; children: string }) => {
  const [done, setDone] = useState(false);
  return (
    <div className="overflow-hidden rounded-[10px] border border-[var(--sn-frame-line)] bg-[var(--sn-frame)] text-[#e6e4dc]">
      <div className="flex items-center justify-between border-b border-[var(--sn-frame-line)] bg-[var(--sn-frame-bar)] px-4 py-2">
        <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-[#9a978d]">{title}</span>
        <button type="button" onClick={() => { navigator.clipboard?.writeText(children).then(() => { setDone(true); setTimeout(() => setDone(false), 1400); }, () => {}); }} className="font-mono text-[11px] text-[#9a978d] hover:text-white">{done ? "Copied" : "Copy"}</button>
      </div>
      <pre className="max-h-[360px] overflow-auto px-4 py-3 font-mono text-[12px] leading-[1.6]">{children}</pre>
    </div>
  );
};

export const Faq = ({ items }: { items: { q: string; a: ReactNode }[] }) => (
  <div className="divide-y divide-[var(--sn-border)] rounded-[10px] border border-[var(--sn-border)] bg-[hsl(var(--card))]">
    {items.map((f) => (
      <details key={f.q} className="group px-5 py-4">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[15px] font-medium text-foreground">
          {f.q}
          <span className="font-mono text-muted-foreground transition-transform group-open:rotate-45">+</span>
        </summary>
        <div className="mt-2.5 max-w-[80ch] text-[14.5px] leading-[1.6] text-[var(--sn-fg2)]">{f.a}</div>
      </details>
    ))}
  </div>
);

/** Shown while a platform file loads: the frame at its final height, so nothing jumps. */
export const Hold = ({ h = 320 }: { h?: number }) => <div className="animate-pulse rounded-[10px] border border-[var(--sn-border)] bg-[color-mix(in_srgb,var(--sn-fg)_3%,transparent)]" style={{ height: h }} />;

/** A page that could not read its file: says so plainly, never a blank. */
export const Unread = ({ what }: { what: string }) => (
  <div className="rounded-[10px] border border-dashed border-[var(--sn-border-strong)] px-5 py-8 text-center text-[14px] text-muted-foreground">
    {what} did not load. Refresh the page, or open it on greeksoup.ai.
  </div>
);

/** Pages of a long table: 50 at a time, with the range and the total. */
export const usePager = <T,>(rows: T[], size = 50) => {
  const [page, setPage] = useState(0);
  const pages = Math.max(1, Math.ceil(rows.length / size));
  const p = Math.min(page, pages - 1);
  const slice = rows.slice(p * size, p * size + size);
  const bar = rows.length > size ? (
    <div className="flex items-center justify-between gap-3 font-mono text-[12px] text-muted-foreground">
      <span>{(p * size + 1).toLocaleString("en-US")}–{Math.min(rows.length, p * size + size).toLocaleString("en-US")} of {rows.length.toLocaleString("en-US")}</span>
      <span className="flex gap-1">
        <button type="button" disabled={p === 0} onClick={() => setPage(p - 1)} className="rounded border border-[var(--sn-border)] px-2.5 py-1 hover:text-foreground disabled:opacity-40">Previous</button>
        <button type="button" disabled={p >= pages - 1} onClick={() => setPage(p + 1)} className="rounded border border-[var(--sn-border)] px-2.5 py-1 hover:text-foreground disabled:opacity-40">Next</button>
      </span>
    </div>
  ) : null;
  return { slice, bar, reset: () => setPage(0) };
};

/** A long page's own index, stuck under the platform bar, marking the section in view. */
export const PageNav = ({ items }: { items: [id: string, label: string][] }) => {
  const [active, setActive] = useState(items[0]?.[0] ?? "");
  const ids = items.map(([id]) => id).join(" ");
  useEffect(() => {
    const els = ids.split(" ").map((id) => document.getElementById(id)).filter(Boolean) as HTMLElement[];
    if (!els.length || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((es) => {
      const seen = es.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (seen[0]) setActive(seen[0].target.id);
    }, { rootMargin: "-160px 0px -55% 0px" });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [ids]);
  return (
    <div className="sticky top-[108px] z-30 border-b border-[var(--sn-border)] bg-background/92 backdrop-blur-xl lg:top-[112px]">
      <W>
        <nav aria-label="On this page" className="flex gap-1 overflow-x-auto [scrollbar-width:none]">
          {items.map(([id, label]) => (
            <a key={id} href={`#${id}`} className={cn("-mb-px whitespace-nowrap border-b-2 px-2.5 py-2.5 text-[13px]", active === id ? "border-primary font-semibold text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>{label}</a>
          ))}
        </nav>
      </W>
    </div>
  );
};
